// This module owns every string the end-of-contest confirm gate shows, so
// the em dash audit, the plural rules, and the wording decisions all live in
// one reviewable place instead of being scattered across six components.
// `human-writing.mdc` bans the em dash and the en dash in every string here.

export const CONFIRM_GATE_TITLE = "Is this how it ended?";
export const CONFIRM_GATE_CONSEQUENCE =
  "Confirm and this is final. You won't be able to undo this yourself.";
export const CONFIRM_GATE_CONFIRM_LABEL = "Confirm result";
export const CONFIRM_GATE_NOT_YET_LABEL = "Not yet";
export const CONFIRM_GATE_REVIVE_LABEL = "Revive somebody instead";
export const CONFIRM_GATE_SPLIT_MEDAL_SUBTITLE =
  "It ended in a tie. This pool splits the medal.";
export const CONFIRM_GATE_NO_WINNER_LINE =
  "The contest ended in a tie. This pool's rule is no winner, so nobody takes the medal.";
export const CONFIRM_GATE_SEASON_TROPHY_LINE =
  "This is the last contest of the season. Confirming also awards the season trophy.";
export const CONFIRM_GATE_AUTO_CONFIRM_DEFAULT =
  "We'll confirm this for you in 7 days if you don't.";
export const CONFIRM_GATE_AUTO_CONFIRM_LAST_DAY =
  "This confirms automatically in less than a day.";
export const CONFIRM_GATE_NAMING_TITLE = "Name the winner";
export const CONFIRM_GATE_NAMING_BODY =
  "The contest ended in a tie. Choose who gets the medal. You can choose more than one.";
// ISSUE-1309. A pick'em tie reaches the naming step only after points, rounds
// won and correct picks all came out level, so the body says so.
export const CONFIRM_GATE_PICKEM_NAMING_BODY =
  "They finished level on points, rounds won and correct picks. Choose who gets the medal. You can choose more than one.";
export const CONFIRM_GATE_NAMING_DISCLOSURE =
  "Everyone in the pool will see this was your call.";
export const CONFIRM_GATE_NAMING_SAVE_LABEL = "Save";
export const CONFIRM_GATE_AUTO_CONFIRMED_TITLE =
  "This contest confirmed automatically after 7 days.";
export const CONFIRM_GATE_AUTO_CONFIRMED_ACTION = "See the results";
export const CONFIRM_GATE_CARD_TITLE =
  "The contest is finished. Review the result.";
export const CONFIRM_GATE_CARD_ACTION = "Review and confirm";
export const CONFIRM_GATE_CARD_PRIVACY =
  "Only you and your Admins can see this.";

export function joinNames(names: readonly string[]): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length === 1) {
    return names[0] ?? "";
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  const allButLast = names.slice(0, -1).join(", ");
  const last = names[names.length - 1];
  return `${allButLast}, and ${last}`;
}

export function buildNudgedStatusLine(input: {
  daysSinceNudge: number;
  daysUntilAutoConfirm: number;
}): string {
  const first =
    input.daysSinceNudge === 0
      ? "We reminded you today."
      : input.daysSinceNudge === 1
        ? "We reminded you 1 day ago."
        : `We reminded you ${input.daysSinceNudge} days ago.`;
  const second =
    input.daysUntilAutoConfirm === 1
      ? "This confirms in 1 day if you don't."
      : `This confirms in ${input.daysUntilAutoConfirm} days if you don't.`;
  return `${first} ${second}`;
}

export function buildNamingWindowWarning(hoursLeft: number): string {
  const unit = hoursLeft === 1 ? "1 hour" : `${hoursLeft} hours`;
  return `Confirming locks these winners for good. You have ${unit} left to change them if you wait.`;
}

export function buildAutoConfirmedSubtitle(input: {
  winnerNames: readonly string[];
  medalName: string;
}): string {
  if (input.winnerNames.length === 0) {
    return `Nobody took the ${input.medalName}. The result is final.`;
  }
  if (input.winnerNames.length === 1) {
    return `${input.winnerNames[0]} holds the ${input.medalName}. The result is final.`;
  }
  return `${joinNames(input.winnerNames)} hold the ${input.medalName}. The result is final.`;
}

export function buildWinsMedalLine(input: {
  winnerNames: readonly string[];
  medalName: string;
}): string {
  if (input.winnerNames.length === 0) {
    return `Nobody takes the ${input.medalName}`;
  }
  if (input.winnerNames.length === 1) {
    return `${input.winnerNames[0]} wins the ${input.medalName}`;
  }
  return `${joinNames(input.winnerNames)} win the ${input.medalName}`;
}

/**
 * ISSUE-868. `/contest/pending-outcome-get` reports `outstandingRound` when the
 * pendingOutcome contest still carries a round that is ungraded and has games.
 * The Commissioner is asked to confirm or wait, so the gate has to say what is
 * still outstanding rather than leave them to choose blind. `roundId` is an
 * internal id and never reaches the screen; it rides along only as a
 * `data-round-id` attribute for tests and debugging.
 *
 * `roundLabel` comes from `/round/list`, which is what identifies a round to a
 * fan on this platform. A null label means the round could not be resolved, so
 * the line degrades to the unnamed wording rather than disappearing: the
 * warning itself is the one thing the Commissioner must never lose.
 */
export function buildOutstandingRoundLine(input: {
  gameCount: number;
  roundLabel: string | null;
}): string {
  const games = input.gameCount === 1 ? "1 game" : `${input.gameCount} games`;
  const opening =
    input.roundLabel === null
      ? "A round hasn't been played yet."
      : `The ${input.roundLabel} round hasn't been played yet.`;
  return `${opening} It still has ${games}. Confirming now locks the result as it stands.`;
}

/**
 * ISSUE-868. One outstanding game, named for the confirm gate's game list. The
 * two names come from `/event/detail-get`'s `data.away.name` and
 * `data.home.name`. Away first, then "at", then home, which is how a matchup is
 * spoken in American English.
 */
export function buildOutstandingGameLine(input: {
  awayName: string;
  homeName: string;
}): string {
  return `${input.awayName} at ${input.homeName}`;
}

export function buildCardCountdownLine(daysUntilAutoConfirm: number): string {
  if (daysUntilAutoConfirm < 1) {
    return "Confirms automatically in less than a day.";
  }
  if (daysUntilAutoConfirm === 1) {
    return "Confirms automatically in 1 day.";
  }
  return `Confirms automatically in ${daysUntilAutoConfirm} days.`;
}

// ISSUE-1325. "Not over yet": the Commissioner cancels the computed end and
// play carries on (overview.md #9, SUR-R61). The result goes away and the
// contest is back in progress; reviving someone is a separate step.
export const CONFIRM_GATE_KEEP_PLAYING_LABEL = "It's not over, keep playing";
export const CONFIRM_GATE_KEEP_PLAYING_LOADING_LABEL = "Reopening play";
export const CONFIRM_GATE_KEEP_PLAYING_CONFIRM_TITLE =
  "Keep this contest going?";
export const CONFIRM_GATE_KEEP_PLAYING_CONFIRM_BODY =
  "The result above goes away and nobody gets a medal yet. The contest goes back to in progress, and you can revive someone from Members so they can play on.";
export const CONFIRM_GATE_KEEP_PLAYING_CONFIRM_LABEL = "Keep playing";
export const CONFIRM_GATE_KEEP_PLAYING_CANCEL_LABEL = "Go back";
function plural(count: number, one: string, many: string): string {
  return count === 1 ? `1 ${one}` : `${String(count)} ${many}`;
}

/**
 * ISSUE-1309. The standings line under a tied pick'em member in the naming
 * step. Every number is the server's (/contest/pending-outcome-get
 * pickemStandings); this only words it.
 */
export function buildPickemTieStandingLine(standing: {
  points: number;
  titles: number;
  wins: number;
}): string {
  return [
    plural(standing.points, "point", "points"),
    plural(standing.titles, "round won", "rounds won"),
    plural(standing.wins, "correct pick", "correct picks"),
  ].join(", ");
}

/** ISSUE-1309. The final-round tiebreaker guess under the standings line. */
export function buildPickemTieGuessLine(standing: {
  finalRoundGuess: number | null;
  finalRoundGuessDiff: number | null;
}): string {
  if (standing.finalRoundGuess === null) {
    return "No guess on the final round";
  }
  const guess = `Final round guess: ${String(standing.finalRoundGuess)}`;
  if (standing.finalRoundGuessDiff === null) {
    return guess;
  }
  return standing.finalRoundGuessDiff === 0
    ? `${guess}, right on`
    : `${guess}, off by ${String(standing.finalRoundGuessDiff)}`;
}
